import { notifications } from '@mantine/notifications';
import { hashKey, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { dispatchAppEvent } from 'src/common/events/appEvents';
import { api } from 'src/common/utils/api';
import { invalidateDataUploadFiles } from 'src/data-capture/hooks/file-uploads/invalidateDataUploadFiles';
import { FoundPersonSummary, UploadedFile } from 'src/data-capture/hooks/file-uploads/types';
import { Detection } from 'src/data-capture/types';

type FoundPerson = FoundPersonSummary & {
  ownItems: Detection[];
  sharedItems: Detection[];
  matchingClient?: { id: string; firstName: string; lastName: string } | null;
};

type AddToFamilyBody = {
  existingClientId?: string;
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string | null;
  familyRoleId: string;
  familyName?: string;
};

type AddToFamily = {
  body: AddToFamilyBody;
  /** The family they join, as the confirmation names it. */
  joining: string;
};

const personPath = (file: UploadedFile, personId: string) => [
  'clients',
  file.clientId,
  'data-upload-files',
  file.id,
  'people',
  personId,
];

export function useFoundPerson(
  file: UploadedFile,
  personId: string,
  { enabled }: { enabled: boolean },
) {
  return useQuery<FoundPerson>({ queryKey: personPath(file, personId), enabled });
}

export function useDismissFoundPerson(file: UploadedFile, personId: string) {
  const queryClient = useQueryClient();
  return useMutation<unknown, Error, void>({
    mutationFn: () => api.post(`${personPath(file, personId).join('/')}/dismiss`),
    onSuccess: () => invalidateDataUploadFiles(queryClient, { clientId: file.clientId }),
  });
}

export function useAddFoundPersonToFamily(file: UploadedFile, person: FoundPersonSummary) {
  const queryClient = useQueryClient();
  const personHash = hashKey(personPath(file, person.id));
  return useMutation<unknown, Error, AddToFamily>({
    mutationFn: ({ body }) =>
      api.post(`${personPath(file, person.id).join('/')}/add-to-family`, body),
    // Adding them takes their row, and the form inside it, off the Documents tab, so a callback passed to
    // `mutate` would not run: what follows a save is done here, before anything is read again.
    onSuccess: (_added, { body, joining }) => {
      if (!body.existingClientId) dispatchAppEvent('CLIENT_CREATED');
      // No count here: a shared cash flow stays on the client's review, so it isn't what was kept.
      notifications.show({
        message: `${person.name} added to ${joining}, ready to review.`,
        color: 'teal',
      });
      // They are no longer open, so reading them again would only find nobody.
      return Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['clients'],
          predicate: query => query.queryHash !== personHash,
        }),
        queryClient.invalidateQueries({ queryKey: ['families'] }),
      ]);
    },
  });
}
